import styles from './metadata-tabs.module.css'

import type { Dispatch } from '@reduxjs/toolkit'
import omit from 'lodash/omit'
import * as React from 'react'
import { connect } from 'react-redux'

import api from '@disco/api/server'
import { TRACK_LOAD_BATCH } from '@disco/constants/ActionTypes'
import {
  CUSTOM_FIELDS,
  CUSTOM_FIELDS_VISIBLE,
} from '@disco/constants/FeatureTypes'
import { TRACK } from '@disco/constants/ItemTypes'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import { saveTrackMetadata } from '@disco/state-management/actions/PlaylistTrackActions'
import {
  addWriterSplit,
  deleteWriterSplit,
  updateEditedTrack,
  updateWriterSplit,
} from '@disco/state-management/actions/ServerActions'
import {
  makeGetFeatureData,
  makeIsFeatureEnabled,
} from '@disco/state-management/selectors/business'
import { getEditableTrackById } from '@disco/state-management/selectors/tracks'
import { getConstanceSettings } from '@disco/state-management/selectors/user'
import { convertMixedMetadataToBasicMetadata } from '@disco/utils/commonMetadata'
import { DISCUSSION_EVENTS } from '@disco/utils/discussion-mixpanel'
import { pushUINotification } from '@disco/utils/notifications'
import { isAggregatedSearchTrack } from '@disco/utils/tracks'

import type { CustomField } from 'types/business'
import type { DiscussionVisibility } from 'types/discussion'
import type { Group } from 'types/group'
import type { Track, TrackId } from 'types/track'

import { sendDiscussionEvent } from 'utils/discussion-mixpanel'

import CustomFieldsTab from 'components/common/tracks/custom-fields'
import EditLyrics from 'components/common/tracks/edit-lyrics'
import MetadataFields from 'components/common/tracks/metadata-fields'
import TrackComments from 'components/common/tracks/track-comments'
import NotesUnreadDot from 'components/common/tracks/track-comments/notes-unread-dot'
import NotesViewedMarker from 'components/common/tracks/track-comments/notes-viewed-marker'
import TrackWriters from 'components/common/tracks/track-writers'
import { Flex, Tabs } from 'components/ds'
import PricingBadge from 'components/ui/pricing-badge'

import { WritersManagement } from 'features/rights-data/components'

import RightsDataPrefetch from './rights-data-prefetch'

function mapStateToProps(state, props): any {
  const getFeatureData = makeGetFeatureData()
  const { isTrial, isFeatureEnabled: customFieldsEnabled } = getFeatureData(
    state,
    {
      featureName: CUSTOM_FIELDS,
    },
  )
  const isTrackEditable = getEditableTrackById(state, {
    id: props.track?.id ?? props.editedTrack?.trackIds[0],
  })

  const isFeatureEnabled = makeIsFeatureEnabled()
  const customFieldsVisible = isFeatureEnabled(state, {
    featureName: CUSTOM_FIELDS_VISIBLE,
  })
  const constanceSettings = getConstanceSettings(state)
  const rightsDataEnabled = constanceSettings?.THIRD_PARTY_RIGHTS_DATA || false
  const recordingTabEnabled =
    constanceSettings?.RIGHTS_DATA_SHOW_RECORDING_TAB || false
  const multiDiscoSearchRightsEnabled =
    constanceSettings?.RIGHTS_DATA_MULTI_DISCO_SEARCH || false
  const splitsV2Enabled = constanceSettings?.SPLITS_V2 || false
  const rightsNewModelEnabled =
    constanceSettings?.TRACK_RIGHTS_HOLDERS_WRITE_TO_NEW_MODELS || false

  return {
    isTrial,
    customFieldsEnabled,
    customFieldsVisible,
    rightsDataEnabled,
    isTrackEditable,
    constanceSettings: constanceSettings,
    splitsV2Enabled,
    attach_writers_to_metadata: state.user.business.attach_writers_to_metadata,
    attach_recording_rights_to_metadata:
      state.user.business.attach_recording_rights_to_metadata,
    rightsNewModelEnabled,
    recordingTabEnabled,
    multiDiscoSearchRightsEnabled,
  }
}

type Props = {
  dispatch: Dispatch
  track?: Track
  isLoading?: boolean
  territories: Group[]
  // TODO replace editedTrack with reducer type
  editedTrack: any
  isTrackEditable: boolean
  originalEditedTrackMetadata?: any
  hasUnsavedChanges?: boolean
  showDifference?: boolean
  hideMoreButton?: boolean
  saving: boolean
  showLyrics?: boolean
  showWriters?: boolean
  tagsTab: React.ReactNode | null | undefined
  territoriesTab: React.ReactNode | null | undefined
  customFields: CustomField[][] | null | undefined
  disableTrackTitle?: boolean
  disableTrackOrder?: boolean
  disableWritersEditing?: boolean
  showSubmitButton?: boolean
  showComments?: boolean
  showExtraMetadata?: boolean
  updateEditedTrack: typeof updateEditedTrack
  saveTrackMetadata: typeof saveTrackMetadata
  sendMixpanelEvent: typeof sendMixpanelEvent
  addWriterSplit: typeof addWriterSplit
  deleteWriterSplit?: typeof deleteWriterSplit
  updateWriterSplit?: typeof updateWriterSplit
  bulkEditingMode?: boolean
  customFieldsVisible?: boolean
  rightsDataEnabled?: boolean
  isTrial?: boolean
  customFieldsEnabled?: boolean
  onClose?: (...args: Array<any>) => any
  updateWriterIds?: (arg0: Array<number>) => void
  constanceSettings: Record<string, any>
  splitsV2Enabled?: boolean
  attach_writers_to_metadata?: boolean
  attach_recording_rights_to_metadata?: boolean
  rightsNewModelEnabled?: boolean
  recordingTabEnabled: boolean
  multiDiscoSearchRightsEnabled?: boolean
  showApplyToAllVersions?: boolean
  nestedTracks?: Array<Partial<Track>>
}
type TabName =
  | 'metadata'
  | 'lyrics'
  | 'rights'
  | 'tags'
  | 'custom'
  | 'notes'
  | 'territory'

const TAB_NAMES: TabName[] = [
  'metadata',
  'lyrics',
  'rights',
  'tags',
  'custom',
  'notes',
  'territory',
]

// Aggregated search tracks belong to another business, so always read-only.
const isRightsReadOnly = (track?: Track): boolean =>
  !!track?.read_only || isAggregatedSearchTrack(track)

const isTabVisible = (name: TabName, props: Props): boolean => {
  const readOnly = props.track?.read_only
  switch (name) {
    case 'metadata':
      return true
    case 'lyrics':
      return !!props.showLyrics && !!props.track
    case 'rights': {
      if (!props.showWriters) return false
      // Multi-DISCO search tracks only show the (read-only) rights tab behind
      // the RIGHTS_DATA_MULTI_DISCO_SEARCH flag.
      if (isAggregatedSearchTrack(props.track)) {
        if (!props.multiDiscoSearchRightsEnabled) return false
        return (
          (props.track?.allow_viewing_publishing_rights ?? true) ||
          (props.track?.allow_viewing_recording_rights ?? true)
        )
      }
      if (!readOnly) return true
      return (
        (props.track?.allow_viewing_publishing_rights ?? true) ||
        ((props.track?.allow_viewing_recording_rights ?? true) &&
          props.recordingTabEnabled)
      )
    }
    case 'tags':
      return !!props.tagsTab && !readOnly
    case 'custom':
      return !!props.customFieldsVisible && !readOnly
    case 'notes':
      return !!props.showComments && !readOnly
    case 'territory':
      return !!props.territoriesTab && !readOnly
  }
}

const getVisibleTabNames = (props: Props): TabName[] =>
  TAB_NAMES.filter(name => isTabVisible(name, props))

class MetadataTabs extends React.Component<
  Props,
  {
    tabValue: TabName
    overrideAlbumArtwork: boolean
    notesVisibility: DiscussionVisibility
    applyToAllVersions: boolean
    titleBeforeApplyToVersions: any
    savedArtworkOverride: boolean | null
    lyricsSavedValue: string
    userModifiedFields: Record<string, boolean>
    // null = still loading; on read-only tracks the tab stays hidden until
    // this resolves so it doesn't flash in then out (WOOF-284).
    rightsTabHasData: boolean | null
  }
> {
  // Read in the constructor before the query string is stripped.
  targetDiscussionId?: number

  constructor(props) {
    super(props)
    const queryParams = new URLSearchParams(window.location.search)
    const tabParam = queryParams.get('tab')
    const noteParam = queryParams.get('note')
    const noteId = noteParam != null ? parseInt(noteParam, 10) : NaN
    this.targetDiscussionId = Number.isFinite(noteId) ? noteId : undefined
    const initialNotesVisibility: DiscussionVisibility =
      queryParams.get('visibility') === 'client' ? 'client' : 'internal'
    const visibleTabs = getVisibleTabNames(props)
    let tabValue: TabName = 'metadata'
    if (tabParam != null) {
      const tabAsNumber = parseInt(tabParam, 10)
      if (Number.isFinite(tabAsNumber) && visibleTabs[tabAsNumber]) {
        tabValue = visibleTabs[tabAsNumber]
      } else if (visibleTabs.includes(tabParam as TabName)) {
        tabValue = tabParam as TabName
      }
    }

    this.state = {
      tabValue,
      overrideAlbumArtwork: false,
      notesVisibility: initialNotesVisibility,
      applyToAllVersions: false,
      titleBeforeApplyToVersions: null,
      savedArtworkOverride: null,
      lyricsSavedValue: props.editedTrack.metadata?.Lyrics?.value ?? '',
      userModifiedFields: {},
      rightsTabHasData: null,
    }
    window.history.replaceState({}, '', `${window.location.pathname}`)
  }

  componentDidMount() {
    if (this.state.tabValue === 'notes') {
      this._sendDiscussionsOpened('url')
    }
  }

  componentDidUpdate(prevProps: Props) {
    // The modal's next/prev arrows swap the track without remounting this
    const trackId = this.props.track?.id
    const prevTrackId = prevProps.track?.id
    if (
      this.state.tabValue === 'notes' &&
      typeof trackId === 'number' &&
      typeof prevTrackId === 'number' &&
      trackId !== prevTrackId
    ) {
      this._sendDiscussionsOpened('tab')
    }

    // Track swap keeps this mounted; reset so the prefetch re-runs for it.
    if (this.props.editedTrack.id !== prevProps.editedTrack.id) {
      this.setState({ rightsTabHasData: null })
    }
  }

  _sendDiscussionsOpened(entryPoint: 'tab' | 'url') {
    const trackId = this.props.track?.id ?? this.props.editedTrack?.id
    const bulkTrackIds: TrackId[] | undefined = this.props.editedTrack?.trackIds
    sendDiscussionEvent(
      DISCUSSION_EVENTS.opened,
      {
        target_type: 'track',
        target_object_id: typeof trackId === 'number' ? trackId : undefined,
        visibility: this.state.notesVisibility,
      },
      {
        entry_point: entryPoint,
        has_target_discussion:
          entryPoint === 'url' && this.targetDiscussionId != null,
        ...(typeof trackId !== 'number' && bulkTrackIds?.length
          ? { bulk: true, track_count: bulkTrackIds.length }
          : {}),
      },
    )
  }

  _handleNotesVisibilityChange = (notesVisibility: DiscussionVisibility) =>
    this.setState({ notesVisibility })

  _handleRightsDataLoaded = (hasData: boolean) => {
    this.setState({ rightsTabHasData: hasData })
    if (!hasData && this.state.tabValue === 'rights') {
      this.setState({ tabValue: 'metadata' })
    }
  }

  _shouldPrefetchRightsData(): boolean {
    return (
      !!this.props.splitsV2Enabled &&
      isRightsReadOnly(this.props.track) &&
      this.state.rightsTabHasData === null &&
      typeof this.props.editedTrack.id === 'number' &&
      isTabVisible('rights', this.props)
    )
  }

  _isRightsTabVisible(): boolean {
    if (!isTabVisible('rights', this.props)) return false
    const readOnly = isRightsReadOnly(this.props.track)
    if (!readOnly) return true
    if (this.state.rightsTabHasData !== null) return this.state.rightsTabHasData
    // Hidden while the fetch is pending so it doesn't flash in then out (WOOF-284).
    return !this._shouldPrefetchRightsData()
  }

  // Fall back to Metadata when the rights tab is the selected one but isn't
  // currently shown (e.g. pending), so no panel renders without its trigger.
  _getActiveTabValue(): TabName {
    if (this.state.tabValue === 'rights' && !this._isRightsTabVisible()) {
      return 'metadata'
    }
    return this.state.tabValue
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (!isTabVisible(this.state.tabValue, nextProps)) {
      this.setState({ tabValue: 'metadata' })
    }
  }

  _updateMetadataField(key: string, value: string) {
    const editedTrack = this.props.editedTrack
    const metadata = editedTrack.metadata
    this.props.updateEditedTrack({
      ...editedTrack,
      metadata: {
        ...metadata,
        [key]: { ...metadata[key], mixed: false, value },
      },
    })
  }

  _handleLyricsModalChange = (value: string) => {
    this._updateMetadataField('Lyrics', value)
  }
  _getOriginalFieldValue = (key: string): string => {
    // Prefer originalEditedTrackMetadata (available in bulk context)
    const fromOriginal = this.props.originalEditedTrackMetadata?.[key]
    if (fromOriginal !== undefined) {
      return fromOriginal?.value === null || fromOriginal?.value === undefined
        ? ''
        : String(fromOriginal.value)
    }
    // Fallback: reconstruct from track (available in single-track context)
    const basicFieldMap: Record<string, string> = {
      Album: 'album',
      Artist: 'artist',
    }
    const trackField = basicFieldMap[key]
    if (trackField && this.props.track) {
      const v = this.props.track[trackField]
      return v === null || v === undefined ? '' : String(v)
    }
    if (this.props.track?.metadata?.[key] !== undefined) {
      const v = this.props.track.metadata[key]
      return v === null || v === undefined ? '' : String(v)
    }
    return ''
  }

  _getMergedEditedTrack = () => {
    const { editedTrack, nestedTracks } = this.props

    if (!this.state.applyToAllVersions || !nestedTracks?.length) {
      return editedTrack
    }

    const editedMetadata = editedTrack.metadata

    // Maps metadata key → Track field name for basic (non-extra) fields.
    // Title and Artwork are excluded: Title per requirements, Artwork handled via override.
    const basicFieldToTrackField: Record<string, string> = {
      Album: 'album',
      Artist: 'artist',
    }

    const mergedMetadata: Record<string, any> = {}

    Object.keys(editedMetadata).forEach(key => {
      if (key === 'Artwork') {
        mergedMetadata[key] = editedMetadata[key]
        return
      }

      if (key === 'Title') {
        mergedMetadata[key] = editedMetadata[key]
        return
      }

      const currentField = editedMetadata[key]
      if (currentField.mixed) {
        mergedMetadata[key] = currentField
        return
      }

      const primaryValue =
        currentField.value === null || currentField.value === undefined
          ? ''
          : String(currentField.value)

      // If the user has explicitly typed in this field, preserve their value
      // regardless of whether it matches the original — prevents the field from
      // re-evaluating as Mixed when the user types back the original value.
      if (this.state.userModifiedFields[key]) {
        mergedMetadata[key] = currentField
        return
      }

      const originalValue = this._getOriginalFieldValue(key)
      if (primaryValue !== originalValue) {
        mergedMetadata[key] = currentField
        return
      }

      let isMixed = false
      for (const nestedTrack of nestedTracks) {
        const trackField = basicFieldToTrackField[key]
        const nestedRawValue = trackField
          ? nestedTrack[trackField]
          : nestedTrack.metadata?.[key]

        const nestedValue =
          nestedRawValue === null || nestedRawValue === undefined
            ? ''
            : String(nestedRawValue)

        if (primaryValue !== nestedValue) {
          isMixed = true
          break
        }
      }

      mergedMetadata[key] = isMixed
        ? { ...currentField, mixed: true, value: '' }
        : currentField
    })

    return { ...editedTrack, metadata: mergedMetadata }
  }

  _handleUpdateEditedTrack = updatedDisplayTrack => {
    if (!this.state.applyToAllVersions) {
      return this.props.updateEditedTrack(updatedDisplayTrack)
    }

    // When applyToAllVersions is on, MetadataFields operates on the merged display
    // track (which may show Mixed fields as { mixed: true, value: '' }). We write
    // only non-mixed fields from the updated display into the real Redux track so
    // Mixed display states never overwrite real Redux values.
    const realEditedTrack = this.props.editedTrack
    // Snapshot the current display state so we can detect fields that were
    // showing as Mixed before this update. A user pasting the same value that
    // already exists in Redux (value unchanged) into a Mixed field must still be
    // treated as an explicit edit — otherwise _getMergedEditedTrack re-evaluates
    // the field as Mixed and the paste appears to silently fail.
    const currentMergedTrack = this._getMergedEditedTrack()
    const newModifiedFields: Record<string, boolean> = {}
    const newMetadata = { ...realEditedTrack.metadata }

    Object.keys(updatedDisplayTrack.metadata).forEach(key => {
      const updated = updatedDisplayTrack.metadata[key]
      if (!updated?.mixed) {
        const valueChanged =
          updated?.value !== realEditedTrack.metadata[key]?.value
        const wasDisplayedAsMixed = !!currentMergedTrack.metadata[key]?.mixed
        if (valueChanged || wasDisplayedAsMixed) {
          newModifiedFields[key] = true
        }
        newMetadata[key] = updated
      }
    })

    if (Object.keys(newModifiedFields).length > 0) {
      this.setState(prev => ({
        userModifiedFields: {
          ...prev.userModifiedFields,
          ...newModifiedFields,
        },
      }))
    }

    return this.props.updateEditedTrack({
      ...realEditedTrack,
      metadata: newMetadata,
    })
  }

  _handleSaveTrackMetadata = opts => {
    this.setState({
      lyricsSavedValue: this.props.editedTrack.metadata?.Lyrics?.value ?? '',
    })
    const applyToAllVersions =
      this.state.applyToAllVersions && this.state.tabValue === 'metadata'
    const { nestedTracks, editedTrack } = this.props
    if (applyToAllVersions && nestedTracks?.length && editedTrack?.metadata) {
      const nestedTrackIds = nestedTracks
        .map(t => t.id)
        .filter((id): id is TrackId => id !== undefined)
      const convertedMetadata = convertMixedMetadataToBasicMetadata(
        editedTrack.metadata,
      )
      const fieldsToOmit = ['Artwork', 'Lyrics', 'Lyrics_source', 'Title']
      // When the recording feature is off the Copyright field is hidden, so
      // propagating it would overwrite each version's preserved value.
      if (!this.props.recordingTabEnabled) {
        fieldsToOmit.push('Copyright')
      }
      const versionMetadata = omit(convertedMetadata, fieldsToOmit)
      Object.keys(versionMetadata).forEach(key => {
        if (Number.isNaN(versionMetadata[key])) {
          delete versionMetadata[key]
        }
      })
      const primaryTrackId = editedTrack.id ?? editedTrack.trackIds?.[0] ?? null
      const artworkField = editedTrack.metadata?.Artwork
      const primaryHasArtwork =
        artworkField && !artworkField.mixed && !!artworkField.value
      api
        .bulkUpdateBatchTrackMetadata(primaryTrackId, nestedTrackIds, {
          metadata: versionMetadata,
          ...(this.state.overrideAlbumArtwork &&
          primaryTrackId &&
          primaryHasArtwork
            ? { track_id_for_artwork: primaryTrackId }
            : {}),
        })
        .then(() => {
          const newAttrs: Partial<Track> = {}
          if ('Artist' in versionMetadata)
            newAttrs.artist = versionMetadata.Artist as string
          if ('Album' in versionMetadata)
            newAttrs.album = versionMetadata.Album as string
          const updatedNestedTracks = nestedTracks
            .filter(
              (t): t is Partial<Track> & { id: TrackId } => t.id !== undefined,
            )
            .map(t => ({
              ...t,
              type: TRACK,
              ...newAttrs,
              metadata: { ...(t.metadata ?? {}), ...versionMetadata },
            }))
          // Update individual nested track entries
          this.props.dispatch({
            type: TRACK_LOAD_BATCH,
            list: updatedNestedTracks,
            resetPlaylistInfo: false,
          })
          // Update parent track's embedded nested_tracks array so selectors
          // that read state.tracks[parentId].nested_tracks see fresh data
          if (primaryTrackId) {
            this.props.dispatch({
              type: TRACK_LOAD_BATCH,
              list: [
                {
                  id: primaryTrackId,
                  type: TRACK,
                  nested_tracks: updatedNestedTracks,
                },
              ],
              resetPlaylistInfo: false,
            })
          }
        })
        .catch(() => {
          pushUINotification({
            message: 'There was an error saving to all versions.',
            level: 'error',
          })
        })
    }

    return this.props.saveTrackMetadata({
      ...opts,
      overrideAlbumArtwork: this.state.overrideAlbumArtwork,
      ...(applyToAllVersions
        ? { successMessage: 'Saved to all versions!' }
        : {}),
    })
  }
  _handleTabChange = (tabValue: string) => {
    if (tabValue === 'notes' && this.state.tabValue !== 'notes') {
      this._sendDiscussionsOpened('tab')
    }
    this.setState({ tabValue: tabValue as TabName })
  }
  _toggleOverrideAlbumArtwork = () => {
    this.setState({
      overrideAlbumArtwork: !this.state.overrideAlbumArtwork,
    })
  }
  _toggleApplyToAllVersions = () => {
    const { editedTrack, originalEditedTrackMetadata } = this.props
    const { applyToAllVersions, titleBeforeApplyToVersions } = this.state

    if (!applyToAllVersions) {
      const currentTitle = editedTrack.metadata?.Title
      const originalTitle =
        originalEditedTrackMetadata?.Title ??
        (this.props.track?.name !== undefined
          ? { mixed: false, value: this.props.track.name }
          : currentTitle)
      this.setState({
        applyToAllVersions: true,
        titleBeforeApplyToVersions: currentTitle,
        overrideAlbumArtwork: this.state.savedArtworkOverride ?? true,
        userModifiedFields: {},
      })
      this.props.updateEditedTrack({
        ...editedTrack,
        metadata: { ...editedTrack.metadata, Title: originalTitle },
      })
    } else {
      this.setState({
        applyToAllVersions: false,
        titleBeforeApplyToVersions: null,
        savedArtworkOverride: this.state.overrideAlbumArtwork,
        userModifiedFields: {},
      })
      if (titleBeforeApplyToVersions) {
        this.props.updateEditedTrack({
          ...editedTrack,
          metadata: {
            ...editedTrack.metadata,
            Title: titleBeforeApplyToVersions,
          },
        })
      }
    }
  }

  render() {
    const {
      showExtraMetadata,
      tagsTab,
      territoriesTab,
      isTrial,
      customFieldsEnabled,
      constanceSettings,
      splitsV2Enabled,
      attach_writers_to_metadata,
      attach_recording_rights_to_metadata,
      rightsNewModelEnabled,
    } = this.props
    const { Lyrics: lyrics } = this.props.editedTrack.metadata
    const notesTrackId =
      typeof this.props.editedTrack?.id === 'number'
        ? this.props.editedTrack.id
        : undefined

    const editedTrackId = this.props.editedTrack.id
    const shouldPrefetchRightsData = this._shouldPrefetchRightsData()

    return (
      <>
        {shouldPrefetchRightsData && (
          <RightsDataPrefetch
            trackId={editedTrackId as number}
            hasIsrc={!!this.props.editedTrack.metadata?.ISRC?.value}
            hasIswc={!!this.props.editedTrack.metadata?.ISWC?.value}
            canViewPublishingRights={
              this.props.track?.allow_viewing_publishing_rights ?? true
            }
            canViewRecordingRights={
              this.props.recordingTabEnabled &&
              (this.props.track?.allow_viewing_recording_rights ?? true)
            }
            onDataLoaded={this._handleRightsDataLoaded}
          />
        )}
        <Tabs.Root
          value={this._getActiveTabValue()}
          onValueChange={this._handleTabChange}
        >
          <Tabs.List className={styles.tabsList}>
            <Tabs.Trigger value="metadata">Metadata</Tabs.Trigger>
            {isTabVisible('lyrics', this.props) && (
              <Tabs.Trigger value="lyrics">Lyrics</Tabs.Trigger>
            )}
            {this._isRightsTabVisible() && (
              <Tabs.Trigger value="rights">
                {this.props.recordingTabEnabled ? 'Rights' : 'Writers'}
              </Tabs.Trigger>
            )}
            {isTabVisible('tags', this.props) && (
              <Tabs.Trigger value="tags">Tags</Tabs.Trigger>
            )}
            {isTabVisible('custom', this.props) && (
              <Tabs.Trigger value="custom">
                {customFieldsEnabled ? (
                  'Custom'
                ) : (
                  <>
                    Custom <PricingBadge title="PRO" />
                  </>
                )}
              </Tabs.Trigger>
            )}
            {isTabVisible('notes', this.props) && (
              <Tabs.Trigger value="notes">
                <Flex as="span" display="inline-flex" align="center" gap="1">
                  Notes
                  {notesTrackId != null && (
                    <NotesUnreadDot trackId={notesTrackId} />
                  )}
                </Flex>
              </Tabs.Trigger>
            )}
            {isTabVisible('territory', this.props) && (
              <Tabs.Trigger value="territory">Territory</Tabs.Trigger>
            )}
          </Tabs.List>

          <Tabs.Content value="metadata">
            <MetadataFields
              track={this.props.track}
              hasUnsavedChanges={
                this.props.hasUnsavedChanges ||
                (this.state.applyToAllVersions &&
                  Object.keys(this.state.userModifiedFields).length > 0)
              }
              originalEditedTrackMetadata={
                this.props.originalEditedTrackMetadata
              }
              editedTrack={this._getMergedEditedTrack()}
              rightsDataEnabled={this.props.rightsDataEnabled}
              saving={this.props.saving}
              showDifference={this.props.showDifference}
              showSubmitButton={this.props.showSubmitButton}
              showExtraMetadata={showExtraMetadata}
              sendMixpanelEvent={this.props.sendMixpanelEvent}
              showArtworkSettings={showExtraMetadata}
              attachWritersToMetadata={attach_writers_to_metadata}
              attachRecordingRightsToMetadata={
                attach_recording_rights_to_metadata
              }
              disableTrackTitle={
                this.props.disableTrackTitle || this.state.applyToAllVersions
              }
              disableTrackOrder={this.props.disableTrackOrder}
              disableArtworkEditing={!showExtraMetadata}
              hideMoreButton={this.props.hideMoreButton}
              updateEditedTrack={this._handleUpdateEditedTrack}
              saveTrackMetadata={this._handleSaveTrackMetadata}
              overrideAlbumArtwork={this.state.overrideAlbumArtwork}
              onOverrideAlbumArtwork={this._toggleOverrideAlbumArtwork}
              constanceSettings={constanceSettings}
              showApplyToAllVersions={this.props.showApplyToAllVersions}
              applyToAllVersions={this.state.applyToAllVersions}
              onApplyToAllVersionsToggle={this._toggleApplyToAllVersions}
              nestedVersionCount={
                this.props.track?.nested_version_count ??
                this.props.nestedTracks?.filter(t => t.nesting_label !== 'Stem')
                  .length
              }
              nestedStemCount={
                this.props.track?.nested_stem_count ??
                this.props.nestedTracks?.filter(t => t.nesting_label === 'Stem')
                  .length
              }
            />
          </Tabs.Content>

          {isTabVisible('lyrics', this.props) && this.props.track && (
            <Tabs.Content value="lyrics">
              <EditLyrics
                track={this.props.track}
                key={this.props.editedTrack.id || 'bulk'}
                disabled={this.props.track.read_only}
                saving={this.props.saving}
                value={lyrics ? lyrics.value : ''}
                initialValue={this.state.lyricsSavedValue}
                onChange={this._handleLyricsModalChange}
                onSubmit={this._handleSaveTrackMetadata}
              />
            </Tabs.Content>
          )}
          {this._isRightsTabVisible() && (
            <Tabs.Content value="rights">
              {splitsV2Enabled ? (
                <WritersManagement
                  key={this.props.editedTrack.id || 'bulk'}
                  editedTrack={this.props.editedTrack}
                  track={this.props.track}
                  saving={this.props.saving}
                  hasUnsavedChanges={this.props.hasUnsavedChanges}
                  updateEditedTrack={this.props.updateEditedTrack}
                  saveTrackMetadata={this._handleSaveTrackMetadata}
                  sendMixpanelEvent={this.props.sendMixpanelEvent}
                  attach_writers_to_metadata={
                    attach_writers_to_metadata ?? false
                  }
                  attach_recording_rights_to_metadata={
                    attach_recording_rights_to_metadata ?? false
                  }
                  rightsNewModelEnabled={rightsNewModelEnabled}
                  readOnly={isRightsReadOnly(this.props.track)}
                />
              ) : (
                <TrackWriters
                  key={this.props.editedTrack.id || 'bulk'}
                  disableEditing={
                    this.props.disableWritersEditing ||
                    isRightsReadOnly(this.props.track)
                  }
                  readOnly={isRightsReadOnly(this.props.track)}
                  editedTrack={this.props.editedTrack}
                  onAdd={this.props.addWriterSplit}
                  onDelete={this.props.deleteWriterSplit}
                  sendMixpanelEvent={this.props.sendMixpanelEvent}
                  saving={this.props.saving}
                  onUpdate={this.props.updateWriterSplit}
                  updateEditedTrack={this.props.updateEditedTrack}
                  saveTrackMetadata={this._handleSaveTrackMetadata}
                  hasUnsavedChanges={this.props.hasUnsavedChanges}
                  bulkEditingMode={this.props.bulkEditingMode}
                  updateWriterIds={this.props.updateWriterIds}
                />
              )}
            </Tabs.Content>
          )}
          {isTabVisible('tags', this.props) && (
            <Tabs.Content value="tags">{tagsTab}</Tabs.Content>
          )}

          {isTabVisible('custom', this.props) && (
            <Tabs.Content value="custom">
              <CustomFieldsTab
                customFields={this.props.customFields}
                track={this.props.track}
                hasUnsavedChanges={this.props.hasUnsavedChanges}
                originalEditedTrackMetadata={
                  this.props.originalEditedTrackMetadata
                }
                editedTrack={this.props.editedTrack}
                saving={this.props.saving}
                showDifference={this.props.showDifference}
                updateEditedTrack={this.props.updateEditedTrack}
                saveTrackMetadata={this._handleSaveTrackMetadata}
                disableTrackOrder={this.props.disableTrackOrder}
                isTrial={isTrial}
                customFieldsEnabled={customFieldsEnabled}
              />
            </Tabs.Content>
          )}

          {isTabVisible('notes', this.props) && (
            <Tabs.Content value="notes">
              {notesTrackId != null && (
                <NotesViewedMarker
                  trackId={notesTrackId}
                  visibility={this.state.notesVisibility}
                />
              )}
              <TrackComments
                editedTrack={this.props.editedTrack}
                trackDurationSeconds={this.props.track?.duration}
                notesVisibility={this.state.notesVisibility}
                onNotesVisibilityChange={this._handleNotesVisibilityChange}
                targetDiscussionId={this.targetDiscussionId}
              />
            </Tabs.Content>
          )}

          {isTabVisible('territory', this.props) && (
            <Tabs.Content value="territory">{territoriesTab}</Tabs.Content>
          )}
        </Tabs.Root>
      </>
    )
  }
}

export default connect(mapStateToProps)(MetadataTabs) as any
